import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import { FOLLOW_UP_PROMPT_EVENT, openFollowupDashboardPopup } from "../../helpers/followUpPrompt";
import AgendamentoModal from "../AgendamentoModal";
import { i18n } from "../../translate/i18n";

const FollowUpPromptDialog = () => {
  const [open, setOpen] = useState(false);
  const [followupModalOpen, setFollowupModalOpen] = useState(false);
  const [popupBlockedOpen, setPopupBlockedOpen] = useState(false);
  const [sourceLabel, setSourceLabel] = useState(null);
  const [seed, setSeed] = useState(null);
  const [resolveFn, setResolveFn] = useState(null);

  // Objeto estável: o AgendamentoModal reage a mudanças da prop `agendamento`,
  // então recriá-lo a cada render limparia o que o usuário já digitou
  const followupSeed = useMemo(
    () => ({ tipo: "followup", ...(seed || {}) }),
    [seed]
  );

  const openFollowupPopupWindow = () => {
    // Autor: GitHub Copilot | Data: 23/03/2026 | Motivo: centralizar abertura da popup de follow-up para reuso no atalho do topo.
    const opened = openFollowupDashboardPopup();

    if (opened) {
      setPopupBlockedOpen(false);
      return true;
    }

    setPopupBlockedOpen(true);
    return false;
  };

  useEffect(() => {
    const handleOpenPrompt = (event) => {
      const incomingSourceLabel = event?.detail?.sourceLabel || null;
      const incomingResolve = event?.detail?.onResolve;

      setSourceLabel(incomingSourceLabel);
      setSeed(event?.detail?.seed || null);
      setResolveFn(() => incomingResolve);
      setOpen(true);
    };

    window.addEventListener(FOLLOW_UP_PROMPT_EVENT, handleOpenPrompt);

    return () => {
      window.removeEventListener(FOLLOW_UP_PROMPT_EVENT, handleOpenPrompt);
    };
  }, []);

  const handleClose = (answer) => {
    if (answer) {
      setFollowupModalOpen(true);
      setOpen(false);
      return;
    }

    if (resolveFn) {
      resolveFn(false);
    }

    setOpen(false);
    setSeed(null);
    setResolveFn(null);
  };

  const handleCloseFollowupModal = () => {
    if (resolveFn) {
      resolveFn(true);
    }
    setFollowupModalOpen(false);
    setSeed(null);
    setResolveFn(null);
  };

  const handleSaveFollowup = () => {
    if (resolveFn) {
      resolveFn(true);
    }
    setFollowupModalOpen(false);
    setSeed(null);
    setResolveFn(null);
  };

  return (
    <>
      <Dialog
        open={popupBlockedOpen}
        onClose={() => setPopupBlockedOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{i18n.t("followUpPrompt.popupBlocked.title")}</DialogTitle>
        <DialogContent>
          <Typography>
            {i18n.t("followUpPrompt.popupBlocked.message")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPopupBlockedOpen(false)} color="default">
            {i18n.t("common.close")}
          </Button>
          <Button onClick={openFollowupPopupWindow} color="primary" variant="contained">
            {i18n.t("followUpPrompt.popupBlocked.tryAgain")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={() => handleClose(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{i18n.t("followUpPrompt.title")}</DialogTitle>
        <DialogContent>
          <Typography>
            {i18n.t("followUpPrompt.question", {
              source: sourceLabel || i18n.t("followUpPrompt.sources.default"),
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleClose(false)} color="default">
            {i18n.t("common.no")}
          </Button>
          <Button onClick={() => handleClose(true)} color="primary" variant="contained">
            {i18n.t("followUpPrompt.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <AgendamentoModal
        open={followupModalOpen}
        onClose={handleCloseFollowupModal}
        agendamento={followupSeed}
        forceFollowupType
        onSave={handleSaveFollowup}
      />
    </>
  );
};

export default FollowUpPromptDialog;
